<template>
    <div class="flex-1">
        <skeleton v-if="planBookingPage === undefined" />
        <not-found v-else-if="planBookingPage === null" />
        <not-found
            v-else-if="planBookingPage.__typename === 'InvalidBookingPage'"
            :title="planBookingPage.title"
            :reason="planBookingPage.reason"
        />
        <not-found
            v-else-if="!planBookingPage.active"
            reason="This plan is inactive. If you are attempting to make a booking or purchase, please contact the facility to resolve the issue."
        />
        <template v-else>
            <purchase-recurring-program
                v-if="isRecurring"
                v-model:contract="recurringContractData"
                :plan="planBookingPage"
                :is-editing="false"
            />
            <purchase-program
                v-else
                v-model:contract="contractData"
                :plan="planBookingPage"
                :is-editing="false"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import Skeleton from 'primevue/skeleton'
import { useRoute } from '#app'
import { computed, ref } from 'vue'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useQuery } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import { type FragmentType, graphql } from '~/resources/graphql'
import PurchaseProgram, {
    PurchaseProgramContractFragment
} from '~/components/PurchaseContract/Program/PurchaseProgram.vue'
import PurchaseRecurringProgram, {
    PurchaseRecurringProgramContractFragment
} from '~/components/PurchaseContract/Program/PurchaseRecurringProgram.vue'
import NotFound from '~/components/NotFound/NotFound.vue'

const route = useRoute()
const slug = computed(() => route.params.slug)
const activeFacility = useActiveFacility()

const query = useRpQuery()
const { data } = useQuery({
    queryKey: ['ProgramsPageQuery', slug, activeFacility],
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query ProgramsPageQuery(
                    $bookingInput: PlanBookingPageInput!
                    $input: SimpleSpokeInput
                ) {
                    planBookingPage(input: $bookingInput) {
                        __typename
                        ... on InvalidBookingPage {
                            title
                            reason
                        }
                        ... on Plan {
                            ...PurchaseProgramPlanFragment
                            ...PurchaseRecurringProgramPlanFragment
                            active
                            terms {
                                billingType
                            }
                        }
                    }
                }
            `),
            {
                facilityId: activeFacility.value.id,
                input: {
                    facilityId: activeFacility.value.id
                },
                bookingInput: {
                    slug: slug.value,
                    facilityId: activeFacility.value.id,
                    planType: 'PROGRAM'
                }
            }
        )
})

const contractData = ref<
    FragmentType<typeof PurchaseProgramContractFragment> | undefined
>()
const recurringContractData =
    ref<FragmentType<typeof PurchaseRecurringProgramContractFragment>>()

const planBookingPage = computed(() => {
    if (data.value === undefined) {
        return undefined
    }

    if (data.value.planBookingPage === null) {
        return null
    }

    return data.value.planBookingPage
})

const isRecurring = computed(() => {
    if (
        planBookingPage.value === undefined ||
        planBookingPage.value === null ||
        planBookingPage.value.__typename !== 'Plan'
    ) {
        throw new Error()
    }

    return planBookingPage.value.terms.billingType === 'RECURRING'
})
</script>
